import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { LearningUnitService } from '../learning-unit.service';
import { tap } from 'rxjs/operators';
import { LearningUnitEditComponent } from '../learning-unit-edit/learning-unit-edit.component';
import { LearningUnitJsonModel } from '../learning-unit.json.model';
import { catchError, filter, of, switchMap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { LearningUnitActions } from './learning-unit.actions';
import {
  ConfirmDialogComponent,
  DeleteDialogData
} from '../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { FetchOptionsStateModel } from '../../core/state/fetchOptions.state';

export interface LearningUnitStateModel {
  learningUnits: LearningUnitJsonModel[]
  learningUnit: LearningUnitJsonModel | null
}

@State<LearningUnitStateModel>({
  name: 'learningUnit',
  defaults: {
    learningUnits: [],
    learningUnit: null
  }
})

@Injectable()
export class LearningUnitState {
  private learningUnitService = inject(LearningUnitService);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);

  @Selector()
  static getState(state: LearningUnitStateModel) {
    return state;
  }

  @Selector()
  static getLearningUnits(state: LearningUnitStateModel) {
    return state.learningUnits;
  }

  @Selector()
  static getLearningUnit(state: LearningUnitStateModel) {
    return state.learningUnit;
  }

  @Selector()
  static getSortedLearningUnits(state: LearningUnitStateModel): LearningUnitJsonModel[] {
    return [...state.learningUnits].sort((a, b) => {
      return a.title.localeCompare(b.title, 'de', { sensitivity: 'base', numeric: true });
    });
  }

  @Selector()
  static getAmountOfLearningUnits(state: LearningUnitStateModel) {
    return state.learningUnits?.length;
  }

  @Action(LearningUnitActions.Fetch)
  onFetchLearningUnits(ctx: StateContext<LearningUnitJsonModel>, action: LearningUnitActions.Fetch) {
    const localFetchOptions = this.store.selectSnapshot<FetchOptionsStateModel>(state => state.fetchOptions);

    const { skipStoreDispatch, ...restOverrides } = action.overrideOptions || {};

    const updatedFetchOptions = {
      ...localFetchOptions,
      ...restOverrides,
      sortFieldName: 'title'
    };

    return this.learningUnitService.getAllWithPagination({
      queryParams: {
        include: action.include
      }
    }, {
      ...updatedFetchOptions,
      skipStoreDispatch
    }).pipe(
      tap(res => {
        ctx.dispatch(new LearningUnitActions.FetchSuccess(res[0]));
      })
    );
  }

  @Action(LearningUnitActions.FetchWithFilters)
  onFetchWithFiltersLearningUnits(ctx: StateContext<LearningUnitJsonModel>, action: LearningUnitActions.FetchWithFilters) {
    return this.learningUnitService.getMany({
      queryParams: {
        filter: action.filters,
        include: 'learningSections'
      }
    }).pipe(
      tap(res => ctx.dispatch(new LearningUnitActions.FetchSuccess(res.data)))
    );
  }

  @Action(LearningUnitActions.FetchSuccess)
  onFetchLearningUnitsSuccess(ctx: StateContext<LearningUnitStateModel>, { payload }: LearningUnitActions.FetchSuccess) {
    ctx.setState({ ...ctx.getState(), learningUnits: payload });
  }

  @Action(LearningUnitActions.Add)
  onAdd(ctx: StateContext<LearningUnitStateModel>, { payload, filters }: LearningUnitActions.Add) {
    return this.dialog.open<LearningUnitEditComponent, LearningUnitJsonModel, LearningUnitJsonModel>(LearningUnitEditComponent, {
      minWidth: '65vw',
      maxHeight: '90vh',
      data: payload
    })
      .afterClosed()
      .pipe(
        filter((learningUnit): learningUnit is LearningUnitJsonModel => !!learningUnit),
        switchMap(learningUnit => this.learningUnitService.createAndSave(learningUnit)),
        catchError(() => of()),
        tap(() => {
          if (filters && Object.keys(filters).length > 0) {
            ctx.dispatch(new LearningUnitActions.FetchWithFilters(filters));
          }
          else {
            ctx.dispatch(new LearningUnitActions.Fetch());
          }
        })
      );
  }

  @Action(LearningUnitActions.Edit)
  onEdit(ctx: StateContext<LearningUnitStateModel>, { payload, filters }: LearningUnitActions.Edit) {
    return this.dialog.open<LearningUnitEditComponent, LearningUnitJsonModel, LearningUnitJsonModel>(LearningUnitEditComponent, {
      minWidth: '65vw',
      maxHeight: '90vh',
      data: payload
    })
      .afterClosed()
      .pipe(
        filter((learningUnit): learningUnit is LearningUnitJsonModel => !!learningUnit),
        switchMap(learningUnit => this.learningUnitService.update(learningUnit)),
        catchError(() => of()),
        tap(() => {
          if (filters && Object.keys(filters).length > 0) {
            ctx.dispatch(new LearningUnitActions.FetchWithFilters(filters));
          }
          else {
            ctx.dispatch(new LearningUnitActions.Fetch());
          }
        })
      );
  }

  @Action(LearningUnitActions.Delete)
  onDelete(ctx: StateContext<LearningUnitJsonModel>, { payload }: LearningUnitActions.Delete) {
    return this.dialog.open<ConfirmDialogComponent, DeleteDialogData, boolean>(ConfirmDialogComponent, {
      data: {
        id: payload.id,
        name: payload.title,
        itemType: 'Lerneinheit',
        deleteFn: () => {
          return payload.destroy();
        }
      }
    })
      .afterClosed()
      .pipe(
        tap(success => {
          if (!success) return;
          ctx.dispatch(new LearningUnitActions.Fetch());
        })
      );
  }

  @Action(LearningUnitActions.FetchOne)
  onFetchOneLearningUnit(ctx: StateContext<LearningUnitStateModel>, action: LearningUnitActions.FetchOne) {
    return this.learningUnitService.getOneWithId(action.id, {}).pipe(
      tap((res: LearningUnitJsonModel) => {
        ctx.dispatch(new LearningUnitActions.FetchOneSuccess(res));
      }),
      catchError((error) => {
        return of(new LearningUnitActions.FetchOneFailure(error));
      })
    );
  }

  @Action(LearningUnitActions.FetchOneSuccess)
  onFetchOneSuccess(ctx: StateContext<LearningUnitStateModel>, { payload }: LearningUnitActions.FetchOneSuccess) {
    ctx.setState({ ...ctx.getState(), learningUnit: payload });
  }

  @Action(LearningUnitActions.FetchOneFailure)
  onFetchOneFailure(ctx: StateContext<LearningUnitStateModel>, { error }: LearningUnitActions.FetchOneFailure) {
    console.error('Failed to fetch learning unit:', error);
  }
}
